import { create } from 'zustand';
import { servicesContainer } from '@/core/di/services-container';
import {
  ApiToken,
  ApiTokenRelation,
  ApiTokenEndpoint,
  CreateTokenRequest,
  UpdateTokenRequest,
  AddRelationRequest,
  AddEndpointRequest,
} from '../types/api-token-types';

interface ApiTokensStore {
  // Data
  tokens: ApiToken[];
  selectedTokenId: number | null;

  // UI states
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  // Dialog states
  createDialogOpen: boolean;
  newlyCreatedToken: string | null;
  deleteTokenId: number | null;
  deleteRelationIds: { tokenId: number; relationId: number } | null;
  deleteEndpointIds: { tokenId: number; endpointId: number } | null;

  // Actions
  fetchTokens: () => Promise<void>;
  createToken: (request: CreateTokenRequest) => Promise<boolean>;
  updateToken: (tokenId: number, request: UpdateTokenRequest) => Promise<boolean>;
  deleteToken: (tokenId: number) => Promise<boolean>;

  addRelation: (tokenId: number, request: AddRelationRequest) => Promise<boolean>;
  deleteRelation: (tokenId: number, relationId: number) => Promise<boolean>;

  addEndpoint: (tokenId: number, request: AddEndpointRequest) => Promise<boolean>;
  deleteEndpoint: (tokenId: number, endpointId: number) => Promise<boolean>;

  setSelectedTokenId: (id: number | null) => void;
  openCreateDialog: () => void;
  closeCreateDialog: () => void;
  clearNewlyCreatedToken: () => void;
  confirmDeleteToken: (tokenId: number) => void;
  cancelDeleteToken: () => void;
  confirmDeleteRelation: (tokenId: number, relationId: number) => void;
  cancelDeleteRelation: () => void;
  confirmDeleteEndpoint: (tokenId: number, endpointId: number) => void;
  cancelDeleteEndpoint: () => void;
  clearError: () => void;
  reset: () => void;
}

export const useApiTokensStore = create<ApiTokensStore>((set, get) => ({
  tokens: [],
  selectedTokenId: null,
  isLoading: false,
  isSaving: false,
  error: null,
  createDialogOpen: false,
  newlyCreatedToken: null,
  deleteTokenId: null,
  deleteRelationIds: null,
  deleteEndpointIds: null,

  fetchTokens: async () => {
    set({ isLoading: true, error: null });
    try {
      const api = servicesContainer.getServices().apiTokensApi;
      const result = await api.getAllTokens();
      if (result.isSuccess) {
        set({ tokens: result.tokens, isLoading: false });
      } else {
        set({ error: result.errorMessage ?? 'Failed to load tokens', isLoading: false });
      }
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load tokens', isLoading: false });
    }
  },

  createToken: async (request: CreateTokenRequest) => {
    set({ isSaving: true, error: null });
    try {
      const api = servicesContainer.getServices().apiTokensApi;
      const result = await api.createToken(request);
      if (result.isSuccess) {
        await get().fetchTokens();
        set({ isSaving: false, createDialogOpen: false, newlyCreatedToken: result.rawToken });
        return true;
      } else {
        set({ error: result.errorMessage ?? 'Failed to create token', isSaving: false });
        return false;
      }
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to create token', isSaving: false });
      return false;
    }
  },

  updateToken: async (tokenId: number, request: UpdateTokenRequest) => {
    set({ isSaving: true, error: null });
    try {
      const api = servicesContainer.getServices().apiTokensApi;
      const result = await api.updateToken(tokenId, request);
      if (result.isSuccess && result.token) {
        set(state => ({
          tokens: state.tokens.map(t => t.tokenId === tokenId ? result.token! : t),
          isSaving: false,
        }));
        return true;
      } else {
        set({ error: result.errorMessage ?? 'Failed to update token', isSaving: false });
        return false;
      }
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to update token', isSaving: false });
      return false;
    }
  },

  deleteToken: async (tokenId: number) => {
    set({ isSaving: true, error: null });
    try {
      const api = servicesContainer.getServices().apiTokensApi;
      const result = await api.deleteToken(tokenId);
      if (result.isSuccess) {
        set(state => ({
          tokens: state.tokens.filter(t => t.tokenId !== tokenId),
          isSaving: false,
          deleteTokenId: null,
          selectedTokenId: state.selectedTokenId === tokenId ? null : state.selectedTokenId,
        }));
        return true;
      } else {
        set({ error: result.errorMessage ?? 'Failed to delete token', isSaving: false, deleteTokenId: null });
        return false;
      }
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to delete token', isSaving: false, deleteTokenId: null });
      return false;
    }
  },

  addRelation: async (tokenId: number, request: AddRelationRequest) => {
    set({ isSaving: true, error: null });
    try {
      const api = servicesContainer.getServices().apiTokensApi;
      const result = await api.addRelation(tokenId, request);
      if (result.isSuccess && result.relation) {
        const newRelation: ApiTokenRelation = result.relation;
        set(state => ({
          tokens: state.tokens.map(t =>
            t.tokenId === tokenId
              ? { ...t, relations: [...t.relations, newRelation] }
              : t
          ),
          isSaving: false,
        }));
        return true;
      } else {
        set({ error: result.errorMessage ?? 'Failed to add relation', isSaving: false });
        return false;
      }
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to add relation', isSaving: false });
      return false;
    }
  },

  deleteRelation: async (tokenId: number, relationId: number) => {
    set({ isSaving: true, error: null });
    try {
      const api = servicesContainer.getServices().apiTokensApi;
      const result = await api.deleteRelation(tokenId, relationId);
      if (result.isSuccess) {
        set(state => ({
          tokens: state.tokens.map(t =>
            t.tokenId === tokenId
              ? { ...t, relations: t.relations.filter(r => r.relationId !== relationId) }
              : t
          ),
          isSaving: false,
          deleteRelationIds: null,
        }));
        return true;
      } else {
        set({ error: result.errorMessage ?? 'Failed to delete relation', isSaving: false, deleteRelationIds: null });
        return false;
      }
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to delete relation', isSaving: false, deleteRelationIds: null });
      return false;
    }
  },

  addEndpoint: async (tokenId: number, request: AddEndpointRequest) => {
    set({ isSaving: true, error: null });
    try {
      const api = servicesContainer.getServices().apiTokensApi;
      const result = await api.addEndpoint(tokenId, request);
      if (result.isSuccess && result.endpoint) {
        const newEndpoint: ApiTokenEndpoint = result.endpoint;
        set(state => ({
          tokens: state.tokens.map(t =>
            t.tokenId === tokenId
              ? { ...t, endpoints: [...t.endpoints, newEndpoint] }
              : t
          ),
          isSaving: false,
        }));
        return true;
      } else {
        set({ error: result.errorMessage ?? 'Failed to add endpoint', isSaving: false });
        return false;
      }
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to add endpoint', isSaving: false });
      return false;
    }
  },

  deleteEndpoint: async (tokenId: number, endpointId: number) => {
    set({ isSaving: true, error: null });
    try {
      const api = servicesContainer.getServices().apiTokensApi;
      const result = await api.deleteEndpoint(tokenId, endpointId);
      if (result.isSuccess) {
        set(state => ({
          tokens: state.tokens.map(t =>
            t.tokenId === tokenId
              ? { ...t, endpoints: t.endpoints.filter(e => e.endpointId !== endpointId) }
              : t
          ),
          isSaving: false,
          deleteEndpointIds: null,
        }));
        return true;
      } else {
        set({ error: result.errorMessage ?? 'Failed to delete endpoint', isSaving: false, deleteEndpointIds: null });
        return false;
      }
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to delete endpoint', isSaving: false, deleteEndpointIds: null });
      return false;
    }
  },

  setSelectedTokenId: (id) => set({ selectedTokenId: id }),
  openCreateDialog: () => set({ createDialogOpen: true, error: null }),
  closeCreateDialog: () => set({ createDialogOpen: false }),
  clearNewlyCreatedToken: () => set({ newlyCreatedToken: null }),
  confirmDeleteToken: (tokenId) => set({ deleteTokenId: tokenId }),
  cancelDeleteToken: () => set({ deleteTokenId: null }),
  confirmDeleteRelation: (tokenId, relationId) => set({ deleteRelationIds: { tokenId, relationId } }),
  cancelDeleteRelation: () => set({ deleteRelationIds: null }),
  confirmDeleteEndpoint: (tokenId, endpointId) => set({ deleteEndpointIds: { tokenId, endpointId } }),
  cancelDeleteEndpoint: () => set({ deleteEndpointIds: null }),
  clearError: () => set({ error: null }),

  reset: () => set({
    tokens: [],
    selectedTokenId: null,
    isLoading: false,
    isSaving: false,
    error: null,
    createDialogOpen: false,
    newlyCreatedToken: null,
    deleteTokenId: null,
    deleteRelationIds: null,
    deleteEndpointIds: null,
  }),
}));
